/**
 * ORDERING BY WHATSAPP OR BY PHONE — the path an older Israeli shopper trusts (2026-10-01).
 *
 * DK's shoppers are 55+ and arrive from Instagram ads inside the app's browser. Twelve of sixteen
 * who reached the checkout in the week to 1 October typed nothing at all. For this audience a
 * message to the shop naming the watch, the colour and the price is a familiar way to buy, and
 * the shop already has the number: the public business payload carries `whatsappNumber`,
 * `callNumber` and `phone` (config.service getSalonBySlug), under the owner's own hero switches.
 * Nothing new is stored. Nothing is sent to anybody but the shop, by the shopper's own tap.
 *
 * The prefilled text is in the shopper's UI language and names what they were looking at — the
 * product, the chosen colour/size, the quantity, the price and the product's address — so the
 * shop can answer without asking "which one?".
 */
import { getWhatsAppUrl, shopChannels } from '../Pages/Shop/ShopActionButtons';
import { isInAppBrowser } from './stableViewport';
import { whatsappHrefFor } from './whatsappLink';
import { isolate } from './shopperText';

/** The blank an open pick leaves for the buyer to fill in, as the ask drafts write it. */
export const WRITE_HERE = '___';

/** `[{ label, value, shown? }]` → " (Colour: Black, Size: M)" or "" — the chosen options, for a message. `shown` is
 *  the value in the shopper's language (utils/shopperText `variantOptionsIn`); a value with a number in it («500 g»)
 *  is bidi-isolated, so an Arabic or Hebrew sentence cannot turn it into «g 500». `separator` is the language's own
 *  comma (run 4, round B: S3 — «لوتس، الوزن», never the Latin «لوتس, الوزن»). An open pick — an option carrying its
 *  `notePrompt` (utils/shopperText) or a `blank` — is followed by the blank the buyer writes in: «نكهة وحدة — ___» (S7). */
export function optionsText(options, separator = ', ') {
  const parts = (Array.isArray(options) ? options : [])
    .filter((o) => o && o.value)
    .map((o) => {
      const raw = String(o.shown || o.value);
      const value = /\d/.test(raw) ? isolate(raw) : raw;
      const said = o.label ? `${o.label}: ${value}` : value;
      // An open pick (S7): the value is said, and a blank follows it for the buyer to write in — after a dash, not a
      // second colon («النوع: نكهة وحدة — ___», fix round 1).
      const blank = o.blank || (o.notePrompt ? WRITE_HERE : '');
      return blank ? `${said} — ${blank}` : said;
    });
  return parts.length ? ` (${parts.join(separator)})` : '';
}

/** The longest note a message carries, «…» included (fix round 1, #4); the checkout's note field takes no more. */
export const NOTE_MAX = 500;

/** A note as a message carries it: one line, at most NOTE_MAX characters (cut with «…»), bidi-isolated so a Latin or
 *  numeric note keeps its own direction inside an Arabic or Hebrew message. '' for none. */
export function noteForMessage(note) {
  const line = typeof note === 'string' ? note.replace(/\s+/g, ' ').trim() : '';
  if (!line) return '';
  const chars = Array.from(line);
  return isolate(chars.length > NOTE_MAX ? `${chars.slice(0, NOTE_MAX - 1).join('').trimEnd()}…` : line);
}

/** The comma a list of options is written with, in the message's language. */
const separatorOf = (t) => {
  const said = typeof t === 'function' ? t('contactOrder.optionsSeparator', { defaultValue: ', ' }) : ', ';
  return typeof said === 'string' && said && said !== 'contactOrder.optionsSeparator' ? said : ', ';
};

/** The words a shopper sends about ONE product. */
export function productOrderMessage({ t, productName, options, quantity, price, url, shopName }) {
  return t('contactOrder.product', {
    defaultValue: 'Hello {{shop}}, I would like to order: {{product}}{{details}} × {{qty}} — {{price}}\n{{url}}',
    shop: shopName || '',
    product: productName || '',
    details: optionsText(options, separatorOf(t)),
    qty: Math.max(1, Number(quantity) || 1),
    price: price || '',
    url: url || '',
  }).replace(/\s+\n/g, '\n').trim();
}

/** The words a shopper sends about a BASKET — one line per item, the buyer's order note, then the total. */
/* THE NOTE GOES WITH THE ORDER (Swe Sosetta run 5, round C: S4). The buyer typed «نكهة الفستق» in «ملاحظة الطلب» for
 * the tart's «نكهة وحدة», and the WhatsApp message still read «صينية تارت (النوع: نكهة وحدة — ___)»: the flavour the
 * owner needed was on the page and not in the message. The note follows the lines, labelled «ملاحظة:». */
export function basketOrderMessage({ t, lines, total, url, shopName, note }) {
  const separator = separatorOf(t);
  const rows = (Array.isArray(lines) ? lines : [])
    .filter((l) => l && l.name)
    .map((l) => `• ${l.name}${optionsText(l.options, separator)} × ${Math.max(1, Number(l.quantity) || 1)}${l.price ? ` — ${l.price}` : ''}`)
    .join('\n');
  const said = noteForMessage(note);
  const noteLine = said ? t('contactOrder.note', { defaultValue: 'Note: {{note}}', note: said }) : '';
  return t('contactOrder.basket', {
    defaultValue: 'Hello {{shop}}, I would like to order:\n{{lines}}\nTotal {{total}}\n{{url}}',
    shop: shopName || '',
    lines: noteLine ? `${rows}\n${noteLine}` : rows,
    total: total || '',
    url: url || '',
  }).replace(/\s+\n/g, '\n').trim();
}

/**
 * Where an order can be placed by hand: the WhatsApp link carrying `text`, the number to call —
 * each only where the shop has it and has not switched that channel off (ShopActionButtons.shopChannels).
 */
/** The number a wa.me link addresses, as a person would dial it: "+972 48259930". */
export function whatsAppNumberText(webUrl) {
  const m = /^https:\/\/wa\.me\/(\d+)/.exec(String(webUrl || ''));
  return m ? `+${m[1]}` : '';
}

/**
 * THE SAME LINK IN EVERY BROWSER (2026-10-14). This used to rewrite the href inside Facebook's and Instagram's web
 * view — first into `whatsapp://`, then into Safari's `x-safari-https://` and WhatsApp's Android intent — and the
 * logs say every one of those failed for a real shopper (utils/whatsappLink.js holds the dates and the counts).
 * wa.me is a universal / app link: tapped in the same frame it opens the app by itself, which is what the Google
 * reviews link on the product page does from inside Instagram. So the channel carries the wa.me link, and the
 * SURFACE's job is only to keep the tap a plain same-frame navigation — no target, no window.open.
 *
 * `inApp` is still answered, because the surfaces need it: it is what drops `target="_blank"` and what starts the
 * "did anything open?" watch behind the fallback sheet.
 */
export function contactOrderChannels(salon, text, { inApp = isInAppBrowser(typeof navigator !== 'undefined' ? navigator.userAgent : '') } = {}) {
  const channels = shopChannels(salon);
  const waPhone = salon?.whatsappNumber ?? salon?.contactNumber ?? salon?.phone ?? '';
  const whatsappWebUrl = channels.showWhatsapp ? getWhatsAppUrl(waPhone, salon?.country, text) : null;
  const whatsappUrl = whatsappHrefFor(whatsappWebUrl);
  // `tel:` gets a number a dialler can read, not the shop's own spelling of it (ShopActionButtons.dialablePhone).
  const callPhone = channels.showCall ? channels.callTel : '';
  return {
    whatsappUrl,
    whatsappWebUrl,
    whatsappNumber: whatsAppNumberText(whatsappWebUrl),
    inApp: !!inApp,
    callHref: callPhone ? `tel:${callPhone}` : null,
    any: !!whatsappUrl || !!callPhone,
  };
}
